import {type Accept, type FileWithPath, useDropzone} from "react-dropzone";
import {useMemo, useState} from "react";
import {IconButton, Paper} from "@mui/material";
import {red} from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";
import {toast} from "react-toastify";

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '20px',
    margin: '5px 0',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: '.15s ease-in-out',
    transitionProperty: 'border, backgroundColor',
    cursor: 'pointer'
};

const focusedStyle = {
    backgroundColor: '#e5f4ff',
    borderColor: '#2196f3'
};

const acceptStyle = {
    borderColor: '#00e676'
};

const rejectStyle = {
    borderColor: '#ff1744'
};

interface Props {
    maxSizeMb: number;
    accept: Accept;
    description: string;
    maxFiles: number;
    onUpload: (data: FileWithPath[]) => void;
}

export function DropZoneBase64({maxSizeMb, accept, maxFiles, description, onUpload}: Props) {
    const [fileData, setFileData] = useState<FileWithPath[]>([]);

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
        isDragActive
    } = useDropzone({
        accept, maxSize: maxSizeMb * 1000 * 1000, maxFiles: maxFiles - fileData.length,
        onDropAccepted: (files) => {
            if (fileData.length + files.length > maxFiles) {
                toast.error(`Превышено максимальное количество файлов: ${maxFiles}`);
                return;
            }
            const newArray = fileData.concat(files);
            setFileData(newArray);
            onUpload(newArray);
        }
    });

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isFocused || isDragActive ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [
        isFocused, isDragAccept, isDragReject, isDragActive
    ]);

    const deleteFile = (index: number) => {
        const filtered = fileData.filter((_, i) => i !== index);
        onUpload(filtered);
        setFileData(filtered);
    };

    return (
        <section className="container">
            <>
                <div {...getRootProps({style})}>
                    <input {...getInputProps()} />
                    <p>
                        Перетащите файл или нажмите, чтобы выбрать
                    </p>
                    {description && (
                        <em>
                            {description}
                        </em>
                    )}
                    {
                        fileData.map((file, index) => (
                                <Paper
                                    key={file.name}
                                    elevation={2}
                                    style={{
                                        margin: 10,
                                        padding: 15,
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '85%'
                                    }}
                                >
                                    <div style={{display: 'flex', flexDirection: 'column'}}>
                                        <a
                                            style={{
                                                margin: 3, color: 'inherit',
                                            }}
                                            href={URL.createObjectURL(file)}
                                            target="_blank"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <b>
                                                {file?.path}
                                            </b>
                                            {' '}
                                            (
                                            {(file.size / 1024 / 1024).toFixed(2)}
                                            {' '}
                                            МБ)
                                        </a>
                                    </div>
                                    <IconButton
                                        color="primary"
                                        aria-label="upload picture"
                                        component="span"
                                        style={{marginLeft: 'auto', color: red[700]}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteFile(index);
                                        }}
                                    >
                                        <CloseIcon/>
                                    </IconButton>
                                </Paper>
                            )
                        )
                    }
                </div>
            </>
        </section>
    );
}